import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Alert,
} from "@mui/material";
import PasskeyFields from "../FormComponents/PasskeyFields";
import { addPasskeyData, editPasskeyData, deletePasskeyData, fetchPasskeyDetailsByParent } from "../../services/passkeyService";

/**
 * AddPasskeyDialog Component
 * Dialog for adding a new passkey entry
 */
export const AddPasskeyDialog = ({
  open,
  onClose,
  sessionId,
  userId,
  onSuccess,
  onError,
  onPasskeyAdded,
}) => {
  const [formData, setFormData] = useState({
    type: "",
    name: "",
    description: "",
    pin: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        type: "",
        name: "",
        description: "",
        pin: "",
      });
      setErrors({});
    }
  }, [open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type) {
      newErrors.type = "Type is required";
    }
    if (!formData.name) {
      newErrors.name = "Name/Username is required";
    }
    if (!formData.description) {
      newErrors.description = "Description is required";
    }
    if (!formData.pin) {
      newErrors.pin = "PIN/Password is required";
    } else if (formData.type === "Passcodes" && !/^\d+$/.test(formData.pin)) {
      newErrors.pin = "PIN must contain only numbers";
    } else if (formData.pin.length < 4) {
      newErrors.pin = "PIN/Password must be at least 4 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
   
      const passkeyData = {
        type: formData.type,
        name: formData.name,
        description: formData.description,
        pin: formData.pin,
        parentid: userId,
      };

      await addPasskeyData(sessionId, passkeyData);

      // Fetch updated list
      const updatedList = await fetchPasskeyDetailsByParent(sessionId, userId);
      onPasskeyAdded(updatedList);

      onSuccess("Passkey added successfully!");
      onClose();
    } catch (error) {
      console.error("Error adding passkey:", error);
      onError("Failed to add passkey. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Passkey</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <PasskeyFields
            formData={formData}
            handleInputChange={handleInputChange}
            prefix=""
            errors={errors}
            disabled={isSubmitting}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting && <CircularProgress size={20} />}
          sx={{ color: "white !important" }}
        >
          {isSubmitting ? "Adding..." : "Add Passkey"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * EditPasskeyDialog Component
 * Dialog for editing an existing passkey entry
 */
export const EditPasskeyDialog = ({
  open,
  onClose,
  passkeyEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onPasskeyUpdated,
}) => {
  const [formData, setFormData] = useState({
    type: "",
    name: "",
    description: "",
    pin: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when entry changes
  useEffect(() => {
    if (open && passkeyEntry) {
      setFormData({
        type: passkeyEntry.type || "",
        name: passkeyEntry.name || "",
        description: passkeyEntry.description || "",
        pin: "", // Never pre-fill PIN for security
      });
      setErrors({});
    }
  }, [open, passkeyEntry]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type) {
      newErrors.type = "Type is required";
    }
    if (!formData.name) {
      newErrors.name = "Name/Username is required";
    }
    if (!formData.description) {
      newErrors.description = "Description is required";
    }
    // PIN is optional during edit (only validate if provided)
    if (formData.pin) {
      if (formData.type === "Passcodes" && !/^\d+$/.test(formData.pin)) {
        newErrors.pin = "PIN must contain only numbers";
      } else if (formData.pin.length < 4) {
        newErrors.pin = "PIN/Password must be at least 4 characters";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const updateData = {
        type: formData.type,
        name: formData.name,
        description: formData.description,
        parentid: userId,
      };

      // Only include PIN if user entered a new one
      if (formData.pin) {
        updateData.pin = formData.pin;
      }

      await editPasskeyData(sessionId, passkeyEntry.id, updateData);

      // Fetch updated list
      const updatedList = await fetchPasskeyDetailsByParent(sessionId, userId);
      onPasskeyUpdated(updatedList);

      onSuccess("Passkey updated successfully!");
      onClose();
    } catch (error) {
      console.error("Error updating passkey:", error);
      onError("Failed to update passkey. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!passkeyEntry) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Passkey</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Leave the PIN/Password field empty if you don't want to change it.
          </Alert>
          <PasskeyFields
            formData={formData}
            handleInputChange={handleInputChange}
            prefix=""
            errors={errors}
            disabled={isSubmitting}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting && <CircularProgress size={20} />}
          sx={{ color: "white !important" }}
        >
          {isSubmitting ? "Updating..." : "Update Passkey"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * DeletePasskeyDialog Component
 * Confirmation dialog for deleting a passkey entry
 */
export const DeletePasskeyDialog = ({
  open,
  onClose,
  passkeyEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onPasskeyDeleted,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deletePasskeyData(sessionId, passkeyEntry.id);

      // Fetch updated list
      const updatedList = await fetchPasskeyDetailsByParent(sessionId, userId);
      onPasskeyDeleted(updatedList);

      onSuccess("Passkey deleted successfully!");
      onClose();
    } catch (error) {
      console.error("Error deleting passkey:", error);
      onError("Failed to delete passkey. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!passkeyEntry) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Passkey</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          Are you sure you want to delete this passkey?
          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <strong>Type:</strong> {passkeyEntry.type}
            <br />
            <strong>Name:</strong> {passkeyEntry.name}
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={isDeleting}
          startIcon={isDeleting && <CircularProgress size={20} />}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
